import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  Card, 
  Row, 
  Col, 
  Button,
  Badge, 
  Spinner,
  Alert,
  Modal,
  Form
} from 'react-bootstrap';
import GenericButton from '../updated/shared/Button';
import { 
  getOfferById, 
  createTrade,
  joinOffer,
  joinOfferWithGateway,
  joinOfferWithAsset,
  confirmDbUsdPayment,
  autoSendCryptoToEscrow,
  sellerJoinBuyOffer,
  getUserProfile,
  refreshWalletData,
  type Offer,
  type UserProfile 
} from '../../services/p2pAPI';
import { getUserWallets, createBuyOrder } from '../../services/api';
import './OfferDetails.css';
import P2PNavigation from './P2PNavigation';
import IconicHeader from '../updated/shared/IconicHeader';

const OfferDetails: React.FC = () => {
  const { offerId } = useParams<{ offerId: string }>();
  const navigate = useNavigate();
  
  // Set background IMMEDIATELY before any state or effects
  if (typeof document !== 'undefined') {
    const body = document.body;
    const root = document.documentElement;
    try {
      const bgColor = getComputedStyle(root).getPropertyValue('--main-body').trim() || '#0a0e27';
      body.style.backgroundColor = bgColor;
      root.style.backgroundColor = bgColor;
    } catch (e) {
      body.style.backgroundColor = '#0a0e27';
      root.style.backgroundColor = '#0a0e27';
    }
  }
  
  const [loading, setLoading] = useState(true);
  const [offer, setOffer] = useState<Offer | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [error, setError] = useState('');
  const [showTradeModal, setShowTradeModal] = useState(false);
  const [tradeAmount, setTradeAmount] = useState('');
  const [tradeLoading, setTradeLoading] = useState(false);
  const [requiredAction, setRequiredAction] = useState<string | null>(null);
  const [tradeId, setTradeId] = useState<string | null>(null);
  const [paymentIntent, setPaymentIntent] = useState<any>(null);
  const [walletBalance, setWalletBalance] = useState<number>(0);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('');
  const [showPaymentOptions, setShowPaymentOptions] = useState(false);
  const [useWalletUsd, setUseWalletUsd] = useState(false);
  // Initialize isCaptainBee from localStorage immediately
  const [isCaptainBee, setIsCaptainBee] = useState(() => {
    if (typeof window !== 'undefined') {
      const userType = localStorage.getItem('userType');
      const userLogged = localStorage.getItem('userlogged');
      return (userType && userType !== 'Indexx Exchange') || 
             userType === 'CaptainBee' || 
             userLogged === 'captain' ||
             userType === 'HoneyUser' || 
             userLogged === 'honeyb';
    }
    return false;
  });

  // Set background color IMMEDIATELY in component body (synchronous)
  // This runs before any effects, ensuring instant background
  if (typeof document !== 'undefined') {
    const root = document.documentElement;
    const body = document.body;
    try {
      const computedStyle = getComputedStyle(root);
      const mainBodyColor = computedStyle.getPropertyValue('--main-body').trim() || '#0a0e27';
      body.style.setProperty('background-color', mainBodyColor, 'important');
      root.style.setProperty('background-color', mainBodyColor, 'important');
    } catch (e) {
      body.style.setProperty('background-color', '#0a0e27', 'important');
      root.style.setProperty('background-color', '#0a0e27', 'important');
    }
  }

  // Set background color SYNCHRONOUSLY before paint to prevent white flash
  useLayoutEffect(() => {
    // Set background synchronously BEFORE browser paints
    const root = document.documentElement;
    const body = document.body;
    const html = document.documentElement;
    
    // Try to get CSS variable value immediately
    let mainBodyColor = '';
    try {
      const computedStyle = getComputedStyle(root);
      mainBodyColor = computedStyle.getPropertyValue('--main-body').trim();
    } catch (e) {
      // Ignore errors
    }
    
    // Set background IMMEDIATELY with fallback
    const bgColor = mainBodyColor || '#0a0e27';
    body.style.setProperty('background-color', bgColor, 'important');
    root.style.setProperty('background-color', bgColor, 'important');
    html.style.setProperty('background-color', bgColor, 'important');
    
    // Don't clean up - keep background to prevent flash when navigating away
  }, []);
  
  // Also inject style tag immediately for extra coverage
  useLayoutEffect(() => {
    // Create a style element that sets background immediately
    const styleId = 'offer-details-bg-fix';
    let styleEl = document.getElementById(styleId);
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = styleId;
      styleEl.textContent = `
        body, html, #root {
          background-color: var(--main-body, #0a0e27) !important;
        }
        .offer-details {
          background-color: var(--main-body, #0a0e27) !important;
        }
      `;
      document.head.appendChild(styleEl);
    }
  }, []);

  useEffect(() => {
    if (offerId) {
      loadOfferDetails();
    }
  }, [offerId]);

  // Apply theme class based on userType (CaptainBee = orange, normal = green)
  // Match the logic used in IconicHeader: if userType !== 'Indexx Exchange', it's CaptainBee
  useEffect(() => {
    const userType = localStorage.getItem('userType');
    const userLogged = localStorage.getItem('userlogged');
    
    // Determine theme class based on userType or userlogged
    // Match IconicHeader logic: userType !== 'Indexx Exchange' means CaptainBee
    let themeClass = 'theme-normal'; // Default to green
    let captainBee = false;
    
    if (userType && userType !== 'Indexx Exchange') {
      // Any userType other than 'Indexx Exchange' is CaptainBee/HoneyUser
      themeClass = 'theme-captain'; // Orange for CaptainBee
      captainBee = true;
    } else if (userType === 'CaptainBee' || userLogged === 'captain') {
      themeClass = 'theme-captain'; // Orange for CaptainBee
      captainBee = true;
    } else if (userType === 'HoneyUser' || userLogged === 'honeyb') {
      themeClass = 'theme-honeyb'; // Orange for HoneyBee
      captainBee = true;
    } else if (userLogged === 'normal' || userType === 'Indexx Exchange') {
      themeClass = 'theme-normal'; // Green for normal users
      captainBee = false;
    }
    
    // Update state for button color
    setIsCaptainBee(captainBee);
    
    // Apply theme class to document body and root
    document.body.classList.remove('theme-normal', 'theme-captain', 'theme-honeyb');
    document.body.classList.add(themeClass);
    document.documentElement.classList.remove('theme-normal', 'theme-captain', 'theme-honeyb');
    document.documentElement.classList.add(themeClass);
    
    // Also apply to root element to ensure CSS variables are available
    const root = document.getElementById('root');
    if (root) {
      root.classList.remove('theme-normal', 'theme-captain', 'theme-honeyb');
      root.classList.add(themeClass);
    }
    
    // Cleanup on unmount
    return () => {
      // Don't remove theme class on unmount as other components might need it
    };
  }, []);

  const loadOfferDetails = async () => {
    try {
      setLoading(true);
      console.log('Loading offer details for ID:', offerId);
      const response = await getOfferById(offerId!);
      console.log('Offer details response:', response);
      
      if (response.success) {
        // Normalize the type to ensure it's 'buy' | 'sell'
        const normalizedOffer = {
          ...response.data,
          type: (response.data.type?.toLowerCase() === 'sell' ? 'sell' : 'buy') as 'buy' | 'sell'
        };
        setOffer(normalizedOffer);
        console.log('Offer set:', normalizedOffer);
        console.log('Payment Method Type:', normalizedOffer.paymentMethodType);
        console.log('Payment Method:', normalizedOffer.paymentMethod);
        console.log('Payment Methods:', normalizedOffer.paymentMethods);
        
        // Load user profile
        try {
          const profileResponse = await getUserProfile(response.data.userEmail);
          if (profileResponse.success) {
            setUserProfile(profileResponse.data);
          }
        } catch (profileError) {
          console.log('Profile not available, using default');
        }
      } else {
        console.error('Failed to load offer:', response.message);
        setError(response.message || 'Failed to load offer');
      }
    } catch (error: any) {
      console.error('Failed to load offer:', error);
      setError(error.message || 'Failed to load offer');
    } finally {
      setLoading(false);
    }
  };

  const loadWalletBalance = async () => {
    try {
      const currentUser = localStorage.getItem('user');
      if (!currentUser) return;
      
      const walletResponse = await getUserWallets(currentUser);
      console.log('Wallet response:', walletResponse);
      
      if (walletResponse.status === 200 && walletResponse.data) {
        const usdWallet = walletResponse.data.find((wallet: any) => 
          wallet.coinSymbol === 'USD' || wallet.coinSymbol === 'USDT'
        );
        if (usdWallet) {
          setWalletBalance(usdWallet.coinBalance || 0);
        }
      }
    } catch (error) {
      console.error('Failed to load wallet balance:', error);
    }
  };

  const handleTakeOffer = async () => {
    if (!offer) return;
    
    // Check if user is trying to trade with themselves
    const currentUser = localStorage.getItem('user');
    if (currentUser === offer.userEmail) {
      setError('You cannot trade with your own offer');
      return;
    }
    
    // Determine payment method type (REAL_FIAT or INTERNAL_USD)
    const paymentType = offer.paymentMethodType || 
      (offer.paymentMethod?.toUpperCase().includes('REAL_FIAT') ? 'REAL_FIAT' : 
       offer.paymentMethod?.toUpperCase().includes('INTERNAL_USD') ? 'INTERNAL_USD' : 
       'REAL_FIAT'); // Default to REAL_FIAT if not specified
    
    // Load wallet balance if it's INTERNAL_USD offer
    if (paymentType === 'INTERNAL_USD') {
    await loadWalletBalance();
    }
    
    // Show payment options
    setShowPaymentOptions(true);
    
    setShowTradeModal(true);
  };

  // Helper function to check if offer uses INTERNAL_USD (Asset Wallet)
  const isInternalUsdOffer = (offer: Offer): boolean => {
    // Check paymentMethodType field first
    if (offer.paymentMethodType) {
      console.log('Payment method type from offer:', offer.paymentMethodType);
      return offer.paymentMethodType === 'INTERNAL_USD';
    }
    
    // Check paymentMethods array for InternalUSD or ASSET_WALLET
    const paymentMethodUpper = (offer.paymentMethod || '').toUpperCase();
    const paymentMethods = offer.paymentMethods || [];
    const allMethods = [paymentMethodUpper, ...paymentMethods.map(m => (m || '').toUpperCase())];
    
    // Check if any payment method is InternalUSD or ASSET_WALLET
    const hasInternalUsd = allMethods.some(method => 
      method === 'INTERNALUSD' || 
      method === 'INTERNAL_USD' || 
      method.includes('INTERNALUSD') || 
      method.includes('INTERNAL_USD') ||
      method === 'ASSET_WALLET' ||
      method.includes('ASSET')
    );
    
    console.log('Checking payment methods:', {
      paymentMethod: offer.paymentMethod,
      paymentMethods: offer.paymentMethods,
      paymentMethodType: offer.paymentMethodType,
      allMethods,
      hasInternalUsd
    });
    
    return hasInternalUsd;
  };

  // Helper function to format payment method names
  const formatPaymentMethod = (method: string): string => {
    if (!method) return 'Bank Transfer';
    const methodUpper = method.toUpperCase().trim();
    
    // Asset Wallet cases
    if (methodUpper === 'ASSET WALLET' || 
        methodUpper === 'ASSET_WALLET' ||
        methodUpper === 'INTERNALUSD' || 
        methodUpper === 'INTERNAL_USD' || 
        methodUpper === 'INTERNAL' ||
        methodUpper.includes('INTERNALUSD') || 
        methodUpper.includes('INTERNAL_USD') ||
        methodUpper.includes('ASSET')) {
      return 'Asset Wallet';
    }
    
    // Real Fiat / Gateway cases - format them nicely
    if (methodUpper === 'REAL_FIAT' || methodUpper === 'REAL FIAT') {
      return 'Gateway';
    }
    if (methodUpper === 'GATEWAY') {
      return 'Gateway';
    }
    if (methodUpper === 'BANK_TRANSFER' || methodUpper === 'BANK TRANSFER') {
      return 'Bank Transfer';
    }
    if (methodUpper === 'FIAT') {
      return 'Fiat';
    }
    
    // Return the method as-is if it doesn't match any special cases
    return method;
  };

  const handleUseWalletUsd = async () => {
    if (!offer || !tradeAmount) return;

    try {
      setTradeLoading(true);
      setError('');

      const amount = parseFloat(tradeAmount);
      if (amount < offer.minAmount || amount > offer.maxAmount) {
        setError(`Amount must be between ${offer.minAmount} and ${offer.maxAmount} ${offer.cryptoType}`);
        return;
      }

      const totalUsd = amount * offer.price;
      
      if (walletBalance < totalUsd) {
        setError('Insufficient USD balance in your wallet');
        return;
      }

      // Verify this is actually an INTERNAL_USD offer before calling join-asset
      const isInternalUsd = isInternalUsdOffer(offer);
      console.log('handleUseWalletUsd - isInternalUsd:', isInternalUsd, 'offer:', offer);
      
      if (!isInternalUsd) {
        setError('This offer requires REAL_FIAT payment method, not asset wallet');
        return;
      }

      // Use joinOfferWithAsset API for INTERNAL_USD offers
      const response = await joinOfferWithAsset(offer.id, amount);

      if (response.success) {
        const tradeId = response.tradeId || response.data?.id || response.data?._id;
        
        // Mark that a trade was created so offers page can refresh
        localStorage.setItem('p2p_trade_created', Date.now().toString());
        
        setShowTradeModal(false);
        navigate(`/p2p/trades/${tradeId}`, {
          state: { 
            message: 'Trade created successfully using asset wallet!',
            walletPayment: true
          }
        });
      } else {
        setError('Failed to create trade');
      }
    } catch (error: any) {
      console.error('Failed to use wallet USD:', error);
      setError(error.message || 'Failed to process payment');
    } finally {
      setTradeLoading(false);
    }
  };

  const handlePaymentMethodSelect = async () => {
    if (!offer || !tradeAmount || !selectedPaymentMethod) return;

    setTradeLoading(true);
    setError('');

    try {
      const amount = parseFloat(tradeAmount);
      if (amount < offer.minAmount || amount > offer.maxAmount) {
        setError(`Amount must be between ${offer.minAmount} and ${offer.maxAmount} ${offer.cryptoType}`);
        return;
      }

      const totalUsd = amount * offer.price;

      // Verify this is actually a REAL_FIAT offer before calling join-gateway
      const isInternalUsd = isInternalUsdOffer(offer);
      console.log('handlePaymentMethodSelect - isInternalUsd:', isInternalUsd, 'offer:', offer);
      
      if (isInternalUsd) {
        setError('This offer requires INTERNAL_USD (asset wallet), not gateway payment');
        return;
      }

      // Use joinOfferWithGateway API for REAL_FIAT offers
      const response = await joinOfferWithGateway(offer.id, amount, selectedPaymentMethod);

      if (!response.success) {
        setError('Failed to create trade');
        return;
      }

      const tradeId = response.tradeId || response.data?.id || response.data?._id;
      
      // Mark that a trade was created so offers page can refresh
      localStorage.setItem('p2p_trade_created', Date.now().toString());

      // Refresh wallet data after trade creation
      const userEmail = localStorage.getItem('user');
      if (userEmail) {
        try {
          await refreshWalletData(userEmail);
        } catch (walletError) {
          console.error('Failed to refresh wallet data:', walletError);
        }
      }

      // For PayPal, create order to get payment link
      if (selectedPaymentMethod === 'PayPal') {
        const orderResponse = await createBuyOrder(
          offer.cryptoType,
          'USD',
          totalUsd,
          amount,
          0,
          '',
          false,
          'Paypal'
        );

        if (orderResponse.status === 200 && orderResponse.data.links) {
          // Extract PayPal approval link
          let payPalPaymentLink = '';
          for (let i = 0; i < orderResponse.data.links.length; i++) {
            if (orderResponse.data.links[i].rel.includes('approve')) {
              payPalPaymentLink = orderResponse.data.links[i].href;
              break;
            }
          }

          if (payPalPaymentLink) {
            setShowTradeModal(false);
            // Navigate to PayPal bridge page
            navigate('/paypal-partnership-with-indexx', {
              state: { payPalPaymentLink, tradeId }
            });
          } else {
            setError('Failed to get PayPal payment link');
          }
        } else {
          setError('Failed to initiate PayPal payment');
        }
      } else {
        // For other payment methods, redirect to their respective pages
        setShowTradeModal(false);
        const methodPath = selectedPaymentMethod.toLowerCase().replace(' ', '-');
        navigate(`/indexx-exchange/payment-${methodPath}`, {
          state: {
            tradeId: tradeId,
            amount: amount,
            price: offer.price,
            totalAmount: totalUsd,
            cryptoType: offer.cryptoType,
            source: 'p2p',
            paymentMethod: selectedPaymentMethod
          }
        });
      }
    } catch (err: any) {
      console.error('Failed to initiate payment:', err);
      setError(err.message || 'Failed to initiate payment');
    } finally {
      setTradeLoading(false);
    }
  };

  const handleCreateTrade = async () => {
    if (!offer || !tradeAmount) return;

    try {
      setTradeLoading(true);
      setError('');

      const amount = parseFloat(tradeAmount);
      if (amount < offer.minAmount || amount > offer.maxAmount) {
        setError(`Amount must be between ${offer.minAmount} and ${offer.maxAmount} ${offer.cryptoType}`);
        return;
      }

      console.log('Creating trade with auto-escrow flow...');
      console.log('Offer payment method:', offer.paymentMethod);
      
      // Step 1: Create trade (this will handle escrow based on offer type)
      const response = await createTrade({
        offerId: offer.id,
        amount: amount,
      });

      if (response.success) {
        const tradeId = response.data.id || response.data._id;
        console.log('Trade created with escrow status:', response.escrowStatus);
        
        // Mark that a trade was created so offers page can refresh
        localStorage.setItem('p2p_trade_created', Date.now().toString());
        
        // Refresh wallet data after trade creation
        const userEmail = localStorage.getItem('user');
        if (userEmail) {
          try {
            await refreshWalletData(userEmail);
            console.log('Wallet data refreshed after trade creation');
          } catch (walletError) {
            console.error('Failed to refresh wallet data:', walletError);
          }
        }
        
        // Step 2: Check if payment method requires external payment (PayPal, Zelle, Wire, etc.)
        const requiresExternalPayment = ['Paypal', 'PayPal', 'Zelle', 'Wire Transfer', 'Wire', 'Venmo', 'ACH'].includes(offer.paymentMethod);
        
        if (requiresExternalPayment && offer.type === 'buy') {
          // BUY offer with external payment - redirect to payment page
          console.log('BUY offer with external payment method:', offer.paymentMethod);
          setError('');
          setShowTradeModal(false);
          
          // For PayPal, redirect to PayPal payment bridge
          if (offer.paymentMethod === 'Paypal' || offer.paymentMethod === 'PayPal') {
            // Navigate to PayPal payment page with trade info
            navigate('/p2p/paypal-payment', {
              state: {
                tradeId: tradeId,
                amount: amount,
                price: offer.price,
                totalAmount: amount * offer.price,
                cryptoType: offer.cryptoType,
                paymentMethod: offer.paymentMethod
              }
            });
          } else {
            // For other payment methods (Zelle, Wire, etc.), redirect to payment page
            const methodPath = offer.paymentMethod.toLowerCase().replace(' ', '-');
            navigate(`/indexx-exchange/payment-${methodPath}`, {
              state: {
                tradeId: tradeId,
                amount: amount,
                price: offer.price,
                totalAmount: amount * offer.price,
                cryptoType: offer.cryptoType,
                source: 'p2p'
              }
            });
          }
          return;
        }
        
        // Step 3: Handle different escrow flows based on offer type
        if (offer.type === 'sell') {
          // SELL offer - buyer joins, crypto should be sent to escrow by seller
          console.log('SELL offer - crypto will be sent to escrow by seller');
          
          setError('');
          setShowTradeModal(false);
          
          if (response.escrowStatus === 'completed') {
            // Trade was automatically completed
            navigate(`/p2p/trades/${tradeId}`, {
              state: { 
                message: 'Trade created and automatically completed! All assets transferred successfully.',
                escrowStatus: 'completed',
                autoCompleted: true
              }
            });
          } else {
            // Trade created but needs manual confirmation
            navigate(`/p2p/trades/${tradeId}`, {
              state: { 
                message: 'Trade created successfully! Payment sent to escrow. Seller will send crypto to escrow.',
                escrowStatus: 'payment_sent'
              }
            });
          }
        } else {
          // BUY offer - seller joins, send crypto to escrow
          console.log('BUY offer - seller joining, sending crypto to escrow');
          
          try {
            await sellerJoinBuyOffer(tradeId);
            console.log('Crypto sent to escrow by seller');
            
            setError('');
            setShowTradeModal(false);
            
            if (response.escrowStatus === 'completed') {
              // Trade was automatically completed
              navigate(`/p2p/trades/${tradeId}`, {
                state: { 
                  message: 'Trade created and automatically completed! All assets transferred successfully.',
                  escrowStatus: 'completed',
                  autoCompleted: true
                }
              });
            } else {
              // Trade created but needs manual confirmation
              navigate(`/p2p/trades/${tradeId}`, {
                state: { 
                  message: 'Trade created successfully! Crypto sent to escrow. Buyer will send payment.',
                  escrowStatus: 'crypto_sent'
                }
              });
            }
          } catch (cryptoError: any) {
            console.error('Failed to send crypto to escrow:', cryptoError);
            // Still navigate to trade details but show warning
            navigate(`/p2p/trades/${tradeId}`, {
              state: { 
                message: 'Trade created but failed to send crypto to escrow.',
                escrowStatus: 'trade_created',
                warning: cryptoError.message
              }
            });
          }
        }
      } else {
        setError(response.message || 'Failed to create trade');
      }
    } catch (error: any) {
      console.error('Failed to create trade:', error);
      setError(error.message || 'Failed to create trade');
    } finally {
      setTradeLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getCryptoIcon = (cryptoType: string) => {
    const icons: { [key: string]: string } = {
      'BTC': 'fab fa-bitcoin',
      'ETH': 'fab fa-ethereum',
      'BNB': 'fas fa-coins',
      'USDT': 'fas fa-dollar-sign',
      'USDC': 'fas fa-dollar-sign',
      'BUSD': 'fas fa-dollar-sign',
    };
    return icons[cryptoType] || 'fas fa-coins';
  };

  const getPaymentMethodIcon = (method: string) => {
    const icons: { [key: string]: string } = {
      'Bank Transfer': 'fas fa-university',
      'PayPal': 'fab fa-paypal',
      'Wise': 'fas fa-exchange-alt',
      'Cash App': 'fas fa-mobile-alt',
      'Zelle': 'fas fa-mobile-alt',
      'Venmo': 'fas fa-mobile-alt',
    };
    return icons[method] || 'fas fa-credit-card';
  };

  // Helper to get background color synchronously (no async, no effects)
  const getBgColor = () => {
    if (typeof window === 'undefined') return '#0a0e27';
    try {
      const computedStyle = getComputedStyle(document.documentElement);
      return computedStyle.getPropertyValue('--main-body').trim() || '#0a0e27';
    } catch (e) {
      return '#0a0e27';
    }
  };

  const bgColor = getBgColor();

  if (loading) {
    return (
      <div 
        className="offer-details offer-details-loading-wrapper" 
        style={{ 
          backgroundColor: bgColor,
          minHeight: '100vh',
          width: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <IconicHeader selectedTab="P2P" onChange={() => {}} />
        <P2PNavigation />
        <div className="offer-details-loading">
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p>Loading Offer Details...</p>
        </div>
      </div>
    );
  }

  if (error && !offer) {
    return (
      <div 
        className="offer-details"
        style={{ 
          backgroundColor: bgColor, 
          minHeight: '100vh',
          width: '100%'
        }}
      >
        <IconicHeader selectedTab="P2P" onChange={() => {}} />
        <P2PNavigation />
        <div className="p2p-content-container">
          <div 
            className="offer-details-error"
            style={{ backgroundColor: bgColor }}
          >
            <Alert variant="danger">
              <Alert.Heading>Error</Alert.Heading>
              <p>{error}</p>
              <hr />
              <div className="d-flex justify-content-end">
              <GenericButton
                text="Back to Offers"
                onClick={() => navigate('/p2p/offers')}
                IconComponent={null}
                className=""
                styles={{}}
                disabled={false}
                loading={false}
              />
              </div>
            </Alert>
          </div>
        </div>
      </div>
    );
  }

  if (!offer) {
    return (
      <div 
        className="offer-details"
        style={{ 
          backgroundColor: bgColor, 
          minHeight: '100vh',
          width: '100%'
        }}
      >
        <IconicHeader selectedTab="P2P" onChange={() => {}} />
        <P2PNavigation />
        <div className="p2p-content-container">
          <div className="offer-details-loading">
            <Spinner animation="border" role="status" variant="primary" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        </div>
      </div>
    );
  }

  const currentUser = localStorage.getItem('user');
  const isOwnOffer = currentUser === offer.userEmail;

  return (
    <div 
      className="offer-details"
      style={{ 
        backgroundColor: bgColor, 
        minHeight: '100vh',
        width: '100%'
      }}
    >
      <IconicHeader selectedTab="P2P" onChange={() => {}} />
      <P2PNavigation />
      <div className="p2p-content-container">

      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      <Row>
        <Col lg={12}>
          <Card className="mb-4 offer-details-card">
            <Card.Header className="offer-header">
              <div className="header-content">
                <div className="header-spacer"></div>
                <div className="header-center">Offer Information</div>
                <div className="header-spacer"></div>
              </div>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col lg={8}>
                  <div className="offer-info-section">
                    <Row>
                      <Col md={6}>
                        <div className="info-item">
                          <div className="info-label">Cryptocurrency</div>
                          <div className="info-value">
                            <i className={`${getCryptoIcon(offer.cryptoType)} me-2`}></i>
                            {offer.cryptoType}
                          </div>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="info-item">
                          <div className="info-label">Payment Method</div>
                          <div className="info-value">
                            <i className={`${getPaymentMethodIcon(offer.paymentMethod)} me-2`}></i>
                            {formatPaymentMethod(offer.paymentMethod)}
                          </div>
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <div className="info-item">
                          <div className="info-label">Price per {offer.cryptoType}</div>
                          <div className="info-value price-value">
                            {formatCurrency(offer.price)}
                          </div>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="info-item">
                          <div className="info-label">Available Amount</div>
                          <div className="info-value">
                            {offer.amount} {offer.cryptoType}
                          </div>
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <div className="info-item">
                          <div className="info-label">Minimum Amount</div>
                          <div className="info-value">
                            {offer.minAmount} {offer.cryptoType}
                          </div>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="info-item">
                          <div className="info-label">Maximum Amount</div>
                          <div className="info-value">
                            {offer.maxAmount} {offer.cryptoType}
                          </div>
                        </div>
                      </Col>
                    </Row>

                    <div className="info-item">
                      <div className="info-label">Trading Terms</div>
                      <div className="info-value terms-text">
                        {offer.terms}
                      </div>
                    </div>

                    {offer.autoReply && (
                      <div className="info-item">
                        <div className="info-label">Auto Reply Message</div>
                        <div className="info-value auto-reply-text">
                          {offer.autoReply}
                        </div>
                      </div>
                    )}
                  </div>
                </Col>
                
                <Col lg={4}>
                  <div className="trade-action-section">
                    <div className="trade-summary">
                      <h6 className="summary-title">Quick Summary</h6>
                      <div className="summary-row">
                        <span>Price per {offer.cryptoType}</span>
                        <span className="price">{formatCurrency(offer.price)}</span>
                      </div>
                      <div className="summary-row">
                        <span>Available</span>
                        <span>{offer.amount} {offer.cryptoType}</span>
                      </div>
                      <div className="summary-row">
                        <span>Min Amount</span>
                        <span>{offer.minAmount} {offer.cryptoType}</span>
                      </div>
                      <div className="summary-row">
                        <span>Max Amount</span>
                        <span>{offer.maxAmount} {offer.cryptoType}</span>
                      </div>
                    </div>

                    <div className="trade-actions">
                      {isOwnOffer ? (
                        <Alert variant="info" className="text-center">
                          This is your own offer
                        </Alert>
                      ) : (
          <GenericButton
            text="Join Offer"
            onClick={handleTakeOffer}
            IconComponent={<i className="fas fa-handshake" style={{ marginRight: '8px' }}></i>}
            className=""
            styles={{}}
            disabled={false}
            loading={false}
          />
                      )}
                    </div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* User Profile Card */}
          {userProfile && (
            <Card className="mb-4">
              <Card.Header>
                <h5>Trader Profile</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <div className="profile-item">
                      <div className="profile-label">Username</div>
                      <div className="profile-value">{userProfile.username}</div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="profile-item">
                      <div className="profile-label">Completion Rate</div>
                      <div className="profile-value">
                        <Badge bg="success">{userProfile.completionRate}%</Badge>
                      </div>
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <div className="profile-item">
                      <div className="profile-label">Total Trades</div>
                      <div className="profile-value">{userProfile.totalTrades}</div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="profile-item">
                      <div className="profile-label">Average Rating</div>
                      <div className="profile-value">
                        {userProfile.averageRating.toFixed(1)} ⭐
                      </div>
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <div className="profile-item">
                      <div className="profile-label">Positive Reviews</div>
                      <div className="profile-value text-success">{userProfile.positiveReviews}</div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="profile-item">
                      <div className="profile-label">Negative Reviews</div>
                      <div className="profile-value text-danger">{userProfile.negativeReviews}</div>
                    </div>
                  </Col>
                </Row>

                <div className="profile-item">
                  <div className="profile-label">Member Since</div>
                  <div className="profile-value">
                    {new Date(userProfile.joinDate).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                    })}
                  </div>
                </div>
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>

      {/* Trade Modal */}
      <Modal show={showTradeModal} onHide={() => setShowTradeModal(false)} centered className="join-offer-modal">
        <Modal.Header closeButton className="join-offer-header">
          <div className="modal-title-content">
            <i className="fas fa-handshake"></i>
          <Modal.Title>Join Offer</Modal.Title>
              <p className="modal-subtitle">{offer.type === 'buy' ? 'Buy' : 'Sell'} {offer.cryptoType} from {offer.username}</p>
          </div>
        </Modal.Header>
        <Modal.Body className="join-offer-body">
          <Form>
            <Form.Group className="mb-4">
              <Form.Label className="form-label-custom">
                <i className="fas fa-coins me-2"></i>
                Amount ({offer.cryptoType})
              </Form.Label>
              <Form.Control
                className="form-control-custom"
                type="number"
                value={tradeAmount}
                onChange={(e) => setTradeAmount(e.target.value)}
                placeholder={`Enter amount (${offer.minAmount} - ${offer.maxAmount})`}
                min={offer.minAmount}
                max={offer.maxAmount}
                step="0.01"
              />
              <Form.Text className="text-muted mt-2 d-block">
                <i className="fas fa-info-circle me-1"></i>
                Minimum: {offer.minAmount} {offer.cryptoType} | Maximum: {offer.maxAmount} {offer.cryptoType}
              </Form.Text>
            </Form.Group>

              <div className="trade-preview">
              <h6 className="preview-title">
                <i className="fas fa-calculator me-2"></i>
                Order Summary
              </h6>
                <div className="preview-row">
                <span className="preview-label">Amount</span>
                <span className="preview-value">{tradeAmount || '0'} {offer.cryptoType}</span>
                </div>
                <div className="preview-row">
                <span className="preview-label">Price per {offer.cryptoType}</span>
                <span className="preview-value">{formatCurrency(offer.price)}</span>
                </div>
              <div className="preview-divider"></div>
                <div className="preview-row total">
                <span className="preview-label">Total</span>
                <span className="preview-value">{formatCurrency(parseFloat(tradeAmount || '0') * offer.price)}</span>
              </div>
            </div>

            {/* Payment Options - Conditional based on paymentMethodType */}
            {showPaymentOptions && offer && (() => {
              const isInternalUsd = isInternalUsdOffer(offer);
              
              // INTERNAL_USD Offer - Show only "Connect Your Asset Wallet" button
              if (isInternalUsd) {
                return (
              <div className="payment-options-section mt-4">
                <h6 className="preview-title mb-3">
                  <i className="fas fa-wallet me-2"></i>
                  Payment Method
                </h6>

                <Alert variant="info" className="wallet-option-alert">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                    <span>
                      <i className="fas fa-wallet me-2"></i>
                      Asset Wallet Balance: ${walletBalance.toFixed(2)}
                    </span>
                  </div>
                  {walletBalance >= (parseFloat(tradeAmount || '0') * offer.price) ? (
                    <GenericButton
                      text={tradeLoading ? "Connecting..." : "Connect Your Asset Wallet"}
                      onClick={() => {
                        setUseWalletUsd(true);
                        handleUseWalletUsd();
                      }}
                      IconComponent={<i className="fas fa-wallet" style={{ marginRight: '8px' }}></i>}
                      className="wallet-usd-btn"
                      styles={{ 
                        backgroundColor: isCaptainBee ? '#FFBB00' : 'var(--primary-color, #11be6a)',
                        color: isCaptainBee ? '#000' : '#fff',
                        width: '100%'
                      }}
                      disabled={tradeLoading || !tradeAmount || parseFloat(tradeAmount || '0') <= 0}
                      loading={tradeLoading}
                    />
                  ) : (
                    <div className="text-warning">
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      Insufficient balance. You need ${(parseFloat(tradeAmount || '0') * offer.price).toFixed(2)} but have ${walletBalance.toFixed(2)}
                    </div>
                  )}
                </Alert>
              </div>
                );
              }
              
              // REAL_FIAT Offer - Show payment gateway cards from offer.paymentMethods
              const availablePaymentMethods = offer.paymentMethods || (offer.paymentMethod ? [offer.paymentMethod] : []);
              
              const getPaymentIcon = (method: string) => {
                const methodLower = method.toLowerCase();
                if (methodLower.includes('paypal') || methodLower === 'paypal') return 'fab fa-paypal';
                if (methodLower.includes('zelle') || methodLower === 'zelle') return 'fas fa-mobile-alt';
                if (methodLower.includes('wire') || methodLower.includes('bank') || methodLower.includes('transfer')) return 'fas fa-university';
                if (methodLower.includes('venmo')) return 'fas fa-mobile-alt';
                if (methodLower.includes('cash app')) return 'fas fa-mobile-alt';
                if (methodLower.includes('wise')) return 'fas fa-exchange-alt';
                return 'fas fa-credit-card';
              };
              
              return (
                <div className="payment-options-section mt-4">
                  <h6 className="preview-title mb-3">
                    <i className="fas fa-credit-card me-2"></i>
                    Payment Method
                  </h6>
                  
                  {availablePaymentMethods.length > 0 ? (
                    <div className="payment-methods-grid mb-3">
                      {availablePaymentMethods.map((method: string) => (
                        <div 
                          key={method}
                          className={`payment-method-card ${selectedPaymentMethod === method ? 'selected' : ''}`}
                          onClick={() => {
                            setSelectedPaymentMethod(selectedPaymentMethod === method ? '' : method);
                          }}
                        >
                          <i className={`${getPaymentIcon(method)} payment-method-icon`}></i>
                          <span>{formatPaymentMethod(method)}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Alert variant="warning">
                      No payment methods available for this offer.
                    </Alert>
                  )}
                </div>
              );
            })()}
          </Form>
        </Modal.Body>
        <Modal.Footer className="join-offer-footer">
            <div className="custom-button-wrapper cancel-wrapper">
            <GenericButton
              text="Cancel"
              onClick={() => setShowTradeModal(false)}
              IconComponent={null}
              className="cancel-btn-modal"
              styles={{ 
                backgroundColor: '#6c757d',
                color: '#fff',
                background: '#6c757d'
              }}
              disabled={false}
              loading={false}
            />
            </div>
            <div className={`custom-button-wrapper ${offer.type === 'buy' ? 'buy-wrapper' : 'sell-wrapper'}`}>
            <GenericButton
              text={tradeLoading ? "Joining..." : "Join Offer"}
                onClick={() => {
                  if (!showPaymentOptions) {
                    handleCreateTrade();
                  } else {
                    const isInternalUsd = isInternalUsdOffer(offer);
                    if (isInternalUsd) {
                      // INTERNAL_USD - "Connect Your Asset Wallet" button handles the API call directly
                      // This button is just for fallback or if needed
                      if (useWalletUsd) {
                        handleUseWalletUsd();
                      }
                    } else {
                      // REAL_FIAT - call join-gateway API with selected payment method
                      if (selectedPaymentMethod) {
                        handlePaymentMethodSelect();
                      }
                    }
                  }
                }}
                disabled={tradeLoading || !tradeAmount || (showPaymentOptions && (() => {
                  const isInternalUsd = isInternalUsdOffer(offer);
                  if (isInternalUsd) {
                    // INTERNAL_USD: Button is disabled, user must click "Connect Your Asset Wallet" button
                    return true; // Disable main join button, use "Connect" button instead
                  } else {
                    // REAL_FIAT: require payment method selection
                    return !selectedPaymentMethod;
                  }
                })())}
              loading={tradeLoading}
              IconComponent={!tradeLoading ? <i className="fas fa-handshake" style={{ marginRight: '8px' }}></i> : null}
                className={offer.type === 'buy' ? 'buy-btn-modal' : 'sell-btn-modal'}
                styles={{ 
                  backgroundColor: 'var(--primary-color, #11be6a)',
                  color: '#fff',
                  background: 'var(--primary-color, #11be6a)'
                }}
              />
            </div>
        </Modal.Footer>
      </Modal>
      </div>
    </div>
  );
};

export default OfferDetails;
